import { updateProduct } from '@/api/products';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useProduct, useProductCategories } from '@/hooks/use-products';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MOCK_SUPPLIERS, PRODUCT_STATUSES, VAT_RATES } from './mock-data';
import type { Product, ProductStatus, VatRate } from './types';

interface FormData {
  status: ProductStatus;
  /** A kiválasztott kategória UUID-ja (`ProductCategory.id`) — ez megy a wire-re. */
  categoryId: string;
  sku: string;
  barcode: string;
  name: string;
  shortName: string;
  description: string;
  brand: string;
  model: string;
  purchasePrice: string;
  sellingPriceNet: string;
  vatRate: VatRate;
  minStockLevel: string;
  reorderQuantity: string;
  location: string;
  supplierId: string;
  // Rental fields
  rentalDailyRate: string;
  rentalWeeklyRate: string;
  rentalMonthlyRate: string;
  rentalDepositAmount: string;
}

function getInitialFormData(product: Product): FormData {
  return {
    status: product.status,
    categoryId: product.categoryId ?? '',
    sku: product.sku,
    barcode: product.barcode ?? '',
    name: product.name,
    shortName: product.shortName ?? '',
    description: product.description ?? '',
    brand: product.brand ?? '',
    model: product.model ?? '',
    purchasePrice: product.purchasePrice.toString(),
    sellingPriceNet: product.sellingPriceNet.toString(),
    vatRate: product.vatRate,
    minStockLevel: product.minStockLevel.toString(),
    reorderQuantity: product.reorderQuantity.toString(),
    location: product.location ?? '',
    supplierId: product.supplier?.id ?? '',
    rentalDailyRate: product.rentalDailyRate?.toString() ?? '',
    rentalWeeklyRate: product.rentalWeeklyRate?.toString() ?? '',
    rentalMonthlyRate: product.rentalMonthlyRate?.toString() ?? '',
    rentalDepositAmount: product.rentalDepositAmount?.toString() ?? '',
  };
}

export function ProductEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { product, isLoading, error, refetch } = useProduct(id);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <p className="text-red-500 mb-4">{t('product.errorPrefix', { message: error })}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
              <Button variant="outline" onClick={() => navigate('/products')}>
                {t('product.backToList')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('product.productNotFound')}
          </h1>
          <Button onClick={() => navigate('/products')} className="mt-4">
            {t('product.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  // Now we can safely use product in initial state
  return <ProductEditForm product={product} navigate={navigate} />;
}

function ProductEditForm({
  product,
  navigate,
}: {
  product: Product;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { t } = useTranslation();
  const {
    categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useProductCategories();
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData(product));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateGrossPrice = () => {
    const net = parseFloat(formData.sellingPriceNet) || 0;
    return Math.round(net * (1 + formData.vatRate / 100));
  };

  const calculateMargin = () => {
    const purchase = parseFloat(formData.purchasePrice) || 0;
    const selling = parseFloat(formData.sellingPriceNet) || 0;
    if (purchase === 0) return 0;
    return (((selling - purchase) / purchase) * 100).toFixed(1);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert(t('product.nameRequired'));
      return;
    }

    if (!formData.sku.trim()) {
      alert(t('product.skuRequiredAlert'));
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const statusMap: Record<ProductStatus, 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED'> = {
        active: 'ACTIVE',
        inactive: 'INACTIVE',
        discontinued: 'DISCONTINUED',
      };

      await updateProduct(product.id, {
        status: statusMap[formData.status],
        // Q-T156: a kategória módosítása a create-tel azonos módon utazik a wire-en.
        categoryId: formData.categoryId || undefined,
        name: formData.name,
        shortName: formData.shortName || undefined,
        description: formData.description || undefined,
        brand: formData.brand || undefined,
        model: formData.model || undefined,
        barcode: formData.barcode || undefined,
        purchasePrice: parseFloat(formData.purchasePrice) || undefined,
        sellingPrice: parseFloat(formData.sellingPriceNet) || undefined,
        vatPercent: formData.vatRate,
        minStockLevel: parseInt(formData.minStockLevel) || undefined,
        reorderQuantity: parseInt(formData.reorderQuantity) || undefined,
      });

      navigate(`/products/${product.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.saveFailed');
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Lásd a ProductCreatePage azonos blokkját: a bérlési díjmezők a kategórián
  // múlnak, de a wire-en nem utaznak (külön `RentalEquipment` entitás) — Q-T158 rokona.
  const selectedCategory = categories.find(c => c.id === formData.categoryId);
  const isRentalEquipment = /BER|bérgép/i.test(
    `${selectedCategory?.code ?? ''} ${selectedCategory?.name ?? ''}`
  );

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/products/${product.id}`)}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('product.editProduct')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {product.sku} - {product.name}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary/90"
            disabled={isSaving}
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {saveError && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {saveError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Status & Category */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.statusAndCategory')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('table.status')}
                  </label>
                  <div className="flex gap-3">
                    {PRODUCT_STATUSES.map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => updateField('status', s.value)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                          formData.status === s.value
                            ? s.color
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.category')}
                  </label>
                  {categoriesLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('common.loading')}
                    </p>
                  ) : categoriesError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {t('product.categoriesLoadError')}
                    </p>
                  ) : categories.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('product.noCategories')}
                    </p>
                  ) : (
                    <div
                      className="flex flex-wrap gap-2"
                      role="group"
                      aria-label={t('common.category')}
                      data-testid="product-category-options"
                    >
                      {categories.map(cat => {
                        const parent = cat.parentId
                          ? categories.find(c => c.id === cat.parentId)
                          : undefined;
                        const selected = formData.categoryId === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => updateField('categoryId', cat.id)}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                              selected
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                            }`}
                          >
                            {parent ? `${parent.name} › ${cat.name}` : cat.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Identifiers */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.identifiers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.skuRequired')}
                  </label>
                  <Input
                    value={formData.sku}
                    onChange={e => updateField('sku', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.barcode')}
                  </label>
                  <Input
                    value={formData.barcode}
                    onChange={e => updateField('barcode', e.target.value)}
                  />
                </div>
                {/* Q-T159: eltávolítva — lásd a ProductCreatePage azonos blokkját.
                    A mező mögött nem volt adatbázis-oszlop, a kitöltése 400-at okozott
                    a mentésnél (a meglévő cikk szerkesztésénél is). */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.locationCode')}
                  </label>
                  <Input
                    value={formData.location}
                    onChange={e => updateField('location', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.productDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.productName')}
                  </label>
                  <Input
                    value={formData.name}
                    onChange={e => updateField('name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.shortName')}
                  </label>
                  <Input
                    value={formData.shortName}
                    onChange={e => updateField('shortName', e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.brand')}
                  </label>
                  <Input
                    value={formData.brand}
                    onChange={e => updateField('brand', e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.typeModel')}
                  </label>
                  <Input
                    value={formData.model}
                    onChange={e => updateField('model', e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.supplier')}
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={e => updateField('supplierId', e.target.value)}
                    className="w-full rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('product.selectPlaceholder')}</option>
                    {MOCK_SUPPLIERS.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={e => updateField('description', e.target.value)}
                    className="w-full rounded-md border p-3 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.pricing')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.purchasePriceNet')}
                  </label>
                  <Input
                    type="number"
                    value={formData.purchasePrice}
                    onChange={e => updateField('purchasePrice', e.target.value)}
                    min={0}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.sellingPriceNet')}
                  </label>
                  <Input
                    type="number"
                    value={formData.sellingPriceNet}
                    onChange={e => updateField('sellingPriceNet', e.target.value)}
                    min={0}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.vatRate')}
                  </label>
                  <select
                    value={formData.vatRate}
                    onChange={e => updateField('vatRate', parseInt(e.target.value) as VatRate)}
                    className="w-full rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                  >
                    {VAT_RATES.map(v => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.sellingPriceGross')}
                  </label>
                  <div className="rounded-md border px-3 py-2 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 font-medium">
                    {formatPrice(calculateGrossPrice())}
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                <p
                  className="text-sm text-green-700 dark:text-green-300"
                  dangerouslySetInnerHTML={{
                    __html: t('product.calculatedMargin', { percent: String(calculateMargin()) }),
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stock settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.stockSettings')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.minStockLevel')}
                  </label>
                  <Input
                    type="number"
                    value={formData.minStockLevel}
                    onChange={e => updateField('minStockLevel', e.target.value)}
                    min={0}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.reorderQuantity')}
                  </label>
                  <Input
                    type="number"
                    value={formData.reorderQuantity}
                    onChange={e => updateField('reorderQuantity', e.target.value)}
                    min={0}
                  />
                </div>
              </div>
              <div className="mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                <p
                  className="text-sm text-blue-700 dark:text-blue-300"
                  dangerouslySetInnerHTML={{
                    __html: t('product.currentStock', {
                      total: String(product.stockQuantity),
                      available: String(product.availableQuantity),
                      reserved: String(product.reservedQuantity),
                    }),
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Rental settings */}
          {isRentalEquipment && (
            <Card>
              <CardHeader>
                <CardTitle>{t('product.rentalSettings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.dailyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalDailyRate}
                      onChange={e => updateField('rentalDailyRate', e.target.value)}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.weeklyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalWeeklyRate}
                      onChange={e => updateField('rentalWeeklyRate', e.target.value)}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.monthlyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalMonthlyRate}
                      onChange={e => updateField('rentalMonthlyRate', e.target.value)}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.depositAmountFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalDepositAmount}
                      onChange={e => updateField('rentalDepositAmount', e.target.value)}
                      min={0}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/products/${product.id}`)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={isSaving}>
              {isSaving ? t('product.savingChanges') : t('product.saveChanges')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
